/**
 * WCC360 v2 — per-screen draft persistence.
 *
 * Some screens (multi-select pickers, rankings) hold their working answer in
 * local state until the learner presses Continue, because a half-made choice
 * is not evidence and must not be recorded as one. That local state used to
 * die the moment the learner navigated away, so an interrupted pick was lost.
 *
 * This hook keeps that working answer in the same cloud-backed store the run
 * itself uses, under a `draft` key namespaced by band + screen. Drafts are
 * never scored: they exist only so the screen can be re-entered exactly as it
 * was left. A committed answer from the store always wins over a draft, and
 * the draft is cleared once the screen commits.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { cloudStorage, isCloudStateReady, subscribeCloudState } from "@/lib/cloudStore";
import type { Band } from "./band";

function draftKey(band: Band, screenId: string) {
  return `wcc360.v2.draft.${band}.${screenId}`;
}

function readDraft<T>(band: Band, screenId: string): T | null {
  try {
    const raw = cloudStorage.getItem(draftKey(band, screenId));
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/**
 * @param committed The answer already committed to the run, if any. When
 *   present it wins: a saved answer is stronger evidence than a draft.
 */
export function useScreenDraft<T>(
  band: Band,
  screenId: string,
  fallback: T,
  committed?: T | null,
) {
  const hasCommitted = committed != null;
  const [value, setValue] = useState<T>(() => {
    if (hasCommitted) return committed as T;
    return readDraft<T>(band, screenId) ?? fallback;
  });

  // The learner's own edits always outrank a late-arriving draft.
  const touched = useRef(false);
  const setDraft = useCallback((next: T | ((prev: T) => T)) => {
    touched.current = true;
    setValue(next);
  }, []);

  // The cloud store can hydrate after mount; pick the draft up when it lands.
  useEffect(() => {
    if (hasCommitted || touched.current || isCloudStateReady()) return;
    return subscribeCloudState(() => {
      if (touched.current || !isCloudStateReady()) return;
      const late = readDraft<T>(band, screenId);
      if (late != null) setValue(late);
    });
  }, [band, screenId, hasCommitted]);

  useEffect(() => {
    try {
      cloudStorage.setItem(draftKey(band, screenId), JSON.stringify(value));
    } catch {
      /* a lost draft must never break the walk */
    }
  }, [band, screenId, value]);

  const clearDraft = useCallback(() => {
    try {
      cloudStorage.removeItem(draftKey(band, screenId));
    } catch {
      /* ignore */
    }
  }, [band, screenId]);

  return [value, setDraft, clearDraft] as const;
}
